import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, PauseCircle, PlayCircle, Building2 } from "lucide-react";

const MODULE_LABELS = {
  emar: "eMAR", training: "Training & Compliance", outcomes: "Person-Centred Outcomes",
  family_portal: "Family Portal", ai_insights: "AI Insights", multi_site: "Multi-Site",
  sso: "SSO / SAML",
};

export default function PlatformOrgProfile() {
  const { orgId } = useParams();
  const { api } = usePlatformAuth();
  const [org, setOrg] = useState(null);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [o, p] = await Promise.all([api.get(`/orgs/${orgId}`), api.get("/plans")]);
    setOrg(o.data); setPlans(p.data);
    setForm({
      name: o.data.name || "",
      plan_id: o.data.plan_id || "",
      seat_limit: o.data.seat_limit ?? o.data.plan?.seat_limit ?? "",
      module_flags: { ...(o.data.module_flags || o.data.plan?.module_flags || {}) },
      primary_contact_name: o.data.primary_contact_name || "",
      primary_contact_email: o.data.primary_contact_email || "",
      primary_contact_phone: o.data.primary_contact_phone || "",
      billing_contact_email: o.data.billing_contact_email || "",
      billing_contact_name:  o.data.billing_contact_name  || "",
      cqc_number: o.data.cqc_number || "",
      provider_registration: o.data.provider_registration || "",
      notes: o.data.notes || "",
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, seat_limit: form.seat_limit === "" ? null : Number(form.seat_limit) };
      const r = await api.patch(`/orgs/${orgId}`, body);
      setOrg(r.data);
      toast.success("Organisation updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const flipStatus = async (target, reason) => {
    try {
      await api.patch(`/orgs/${orgId}/status`, { status: target, reason });
      toast.success(`Status updated to ${target}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed"); }
  };

  if (!org) return <div className="p-6 text-slate-500 text-sm" data-testid="orgprofile-loading">Loading organisation…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="org-profile">
      <div className="flex items-center gap-3">
        <Link to="/platform-admin/organisations" className="text-slate-400 hover:text-slate-100 text-xs inline-flex items-center gap-1" data-testid="orgprofile-back">
          <ArrowLeft className="w-3.5 h-3.5" /> All organisations
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Customer</div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2" data-testid="orgprofile-name">
            <Building2 className="w-5 h-5 text-slate-400" /> {org.name}
          </h1>
          <div className="text-[11px] font-mono text-slate-500 mt-1">{org.id}</div>
        </div>
        <div className="flex items-center gap-2">
          {org.status === "active" ? (
            <button onClick={() => flipStatus("suspended", window.prompt("Reason for suspension:") || "")}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-rose-300 border border-rose-500/40 hover:bg-rose-500/10"
                    data-testid="btn-suspend">
              <PauseCircle className="w-3.5 h-3.5" /> Suspend
            </button>
          ) : (
            <button onClick={() => flipStatus("active", "Restored")}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10"
                    data-testid="btn-restore">
              <PlayCircle className="w-3.5 h-3.5" /> Restore
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <Metric label="Users"         value={org.stats?.users}         testid="metric-users" />
        <Metric label="Sites"         value={org.stats?.sites}         testid="metric-sites" />
        <Metric label="Service Users" value={org.stats?.service_users} testid="metric-service-users" />
        <Metric label="Status" value={<span className="capitalize">{org.status}</span>} testid="metric-status" />
      </div>

      {/* Organisation & plan */}
      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3" data-testid="section-org-details">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Organisation details</div>
        <Field label="Name">
          <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="orgprofile-name-input" />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="CQC number">
            <input value={form.cqc_number || ""} onChange={(e) => setForm({ ...form, cqc_number: e.target.value })} className={inputCls} data-testid="orgprofile-cqc" />
          </Field>
          <Field label="Provider registration">
            <input value={form.provider_registration || ""} onChange={(e) => setForm({ ...form, provider_registration: e.target.value })} className={inputCls} />
          </Field>
        </div>
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3" data-testid="section-plan">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Subscription plan & seats</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Plan">
            <select value={form.plan_id || ""} onChange={(e) => {
              const p = plans.find((x) => x.id === e.target.value);
              setForm({ ...form, plan_id: e.target.value,
                        seat_limit: p?.seat_limit ?? form.seat_limit,
                        module_flags: p?.module_flags ? { ...p.module_flags } : form.module_flags });
            }} className={inputCls} data-testid="orgprofile-plan">
              <option value="">— Select a plan —</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} (£{(p.price_pence/100).toFixed(0)}/{p.interval})</option>)}
            </select>
          </Field>
          <Field label="Seat limit">
            <input type="number" min={1} value={form.seat_limit ?? ""} onChange={(e) => setForm({ ...form, seat_limit: e.target.value })} className={inputCls} data-testid="orgprofile-seat-limit" />
          </Field>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Enabled modules</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="module-flags">
            {Object.keys(MODULE_LABELS).map((k) => {
              const on = !!form.module_flags?.[k];
              return (
                <label key={k} className={`flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-sm ${on ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-200" : "bg-slate-950 border-slate-800 text-slate-300"}`}>
                  <input type="checkbox" checked={on} onChange={(e) => setForm({
                    ...form, module_flags: { ...(form.module_flags || {}), [k]: e.target.checked },
                  })} data-testid={`module-flag-${k}`} />
                  {MODULE_LABELS[k]}
                </label>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3" data-testid="section-contacts">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Contacts</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Primary contact name"><input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} className={inputCls} /></Field>
          <Field label="Primary contact email"><input value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} className={inputCls} data-testid="orgprofile-primary-email" /></Field>
          <Field label="Primary contact phone"><input value={form.primary_contact_phone} onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })} className={inputCls} /></Field>
          <Field label="Billing contact name"><input value={form.billing_contact_name} onChange={(e) => setForm({ ...form, billing_contact_name: e.target.value })} className={inputCls} /></Field>
          <Field label="Billing contact email"><input value={form.billing_contact_email} onChange={(e) => setForm({ ...form, billing_contact_email: e.target.value })} className={inputCls} /></Field>
        </div>
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-2" data-testid="section-sites">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Sites ({org.site_list?.length || 0})</div>
        {org.site_list?.length ? (
          <ul className="text-sm space-y-1">
            {org.site_list.map((s) => (
              <li key={s.id} className="flex justify-between border-b border-slate-800/50 py-1">
                <span>{s.name}</span>
                <span className="text-slate-500 text-xs">{s.phone || s.address || ""}</span>
              </li>
            ))}
          </ul>
        ) : <div className="text-xs text-slate-500">No sites yet.</div>}
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="section-audit">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Platform audit history — recent</div>
        {org.recent_audit?.length ? (
          <ul className="text-xs font-mono space-y-1">
            {org.recent_audit.map((a) => (
              <li key={a.id} className="text-slate-300">
                <span className="text-slate-500">{a.at?.slice(0, 19).replace('T', ' ')}</span>{" "}
                <span className="text-emerald-400">{a.action}</span>{" "}
                <span className="text-slate-400">by {a.actor_email}</span>
                {a.summary && <span className="text-slate-500"> — {a.summary}</span>}
              </li>
            ))}
          </ul>
        ) : <div className="text-xs text-slate-500">No audit events yet.</div>}
      </section>

      <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-slate-950/95 border-t border-slate-800 backdrop-blur">
        <button onClick={save} disabled={saving} data-testid="orgprofile-save"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full h-9 px-3 rounded-md bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:outline-none text-sm";

function Metric({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className="text-2xl font-heading font-semibold mt-1 tabular-nums">{value ?? 0}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</span>
      {children}
    </label>
  );
}
